import { Fragment, useState } from 'react';
import { useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';
import { ChevronRight, ShieldAlert, ShieldCheck } from 'lucide-react';
import clsx from 'clsx';
import type { AuditEntry, AuditIntegrity, UserRow } from '@tengesa/shared';
import { api } from '../../lib/api';
import { useSession } from '../../app/session';
import { Button, EmptyState, ErrorNotice, Field, PageHeader, Panel, Select, Spinner, TextInput, formatDateTime } from '../../ui';
import { ACTION_FILTERS, actionLabel } from './actions';

interface AuditPage {
  items: AuditEntry[];
  nextBefore: number | null;
}

/** Converts a yyyy-mm-dd picked in Harare time into an ISO instant. */
function harareDay(date: string, endOfDay: boolean): string {
  return new Date(`${date}T${endOfDay ? '23:59:59.999' : '00:00:00.000'}+02:00`).toISOString();
}

export function AuditPage() {
  const { can } = useSession();
  const [action, setAction] = useState('');
  const [userId, setUserId] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [open, setOpen] = useState<number | null>(null);

  const users = useQuery({ queryKey: ['users'], queryFn: () => api.get<UserRow[]>('/api/users'), enabled: can('users.view') });
  const q = useInfiniteQuery({
    queryKey: ['audit', action, userId, from, to],
    initialPageParam: null as number | null,
    queryFn: ({ pageParam }) => {
      const p = new URLSearchParams({ limit: '50' });
      if (action) p.set('action', action);
      if (userId) p.set('userId', userId);
      if (from) p.set('from', harareDay(from, false));
      if (to) p.set('to', harareDay(to, true));
      if (pageParam) p.set('before', String(pageParam));
      return api.get<AuditPage>(`/api/audit?${p}`);
    },
    getNextPageParam: (last) => last.nextBefore,
  });
  const verify = useMutation({ mutationFn: () => api.get<AuditIntegrity>('/api/audit/verify') });
  const items = q.data?.pages.flatMap((p) => p.items) ?? [];
  const filtered = !!(action || userId || from || to);

  return (
    <>
      <PageHeader
        title="Audit log"
        description="A permanent record of who did what. Entries can't be edited or deleted, and each one is fingerprinted so tampering shows up."
        actions={
          <Button onClick={() => verify.mutate()} busy={verify.isPending}>
            Check integrity
          </Button>
        }
      />
      {verify.data && (
        <div
          className={clsx(
            'mb-5 flex items-start gap-3 rounded-[var(--radius-panel)] border p-4',
            verify.data.ok ? 'border-ok/30 bg-ok-soft text-ok' : 'border-danger/30 bg-danger-soft text-danger',
          )}
          role="status"
        >
          {verify.data.ok ? <ShieldCheck className="size-5 shrink-0" /> : <ShieldAlert className="size-5 shrink-0" />}
          <p>
            {verify.data.ok
              ? `Checked ${verify.data.checked.toLocaleString()} entries. Every entry matches its fingerprint.`
              : `Entry #${verify.data.firstBrokenSeq} has been altered outside Tengesa. Entries before it are intact. Restore from backup and investigate.`}
          </p>
        </div>
      )}
      {verify.error && <ErrorNotice error={verify.error} />}

      <Panel>
        <div className="grid gap-3 border-b border-line p-4 sm:grid-cols-2 lg:grid-cols-[1.2fr_1.2fr_1fr_1fr_auto] lg:items-end">
          <Field label="Activity" htmlFor="f-action">
            <Select id="f-action" value={action} onChange={(e) => setAction(e.target.value)}>
              {ACTION_FILTERS.map((a) => (
                <option key={a.value} value={a.value}>
                  {a.label}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Done by" htmlFor="f-user">
            <Select id="f-user" value={userId} onChange={(e) => setUserId(e.target.value)} disabled={!users.data}>
              <option value="">Anyone</option>
              {users.data?.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.fullName}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="From" htmlFor="f-from">
            <TextInput id="f-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          </Field>
          <Field label="To" htmlFor="f-to">
            <TextInput id="f-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </Field>
          <Button
            variant="ghost"
            disabled={!filtered}
            onClick={() => {
              setAction('');
              setUserId('');
              setFrom('');
              setTo('');
            }}
          >
            Clear
          </Button>
        </div>

        {q.isLoading && <Spinner />}
        {q.error && (
          <div className="p-4">
            <ErrorNotice error={q.error} onRetry={() => q.refetch()} />
          </div>
        )}
        {q.data && items.length === 0 && <EmptyState title="Nothing recorded for these filters">Widen the dates or clear the filters.</EmptyState>}
        {items.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] text-left">
              <thead>
                <tr className="border-b border-line text-sm text-muted">
                  <th className="w-8 px-3 py-2.5" />
                  <th className="px-2 py-2.5 font-medium">When</th>
                  <th className="px-3 py-2.5 font-medium">What happened</th>
                  <th className="px-3 py-2.5 font-medium">By</th>
                  <th className="px-5 py-2.5 text-right font-medium">#</th>
                </tr>
              </thead>
              <tbody>
                {items.map((e) => {
                  const expandable = e.before != null || e.after != null || !!e.ip;
                  const isOpen = open === e.seq;
                  return (
                    <Fragment key={e.seq}>
                      <tr className={clsx('border-b border-line align-top', expandable && 'cursor-pointer hover:bg-ledger-50/60')} onClick={() => expandable && setOpen(isOpen ? null : e.seq)}>
                        <td className="px-3 py-3">
                          {expandable && (
                            <button aria-expanded={isOpen} aria-label="Show details" className="text-muted" onClick={(ev) => { ev.stopPropagation(); setOpen(isOpen ? null : e.seq); }}>
                              <ChevronRight className={clsx('size-4 transition-transform', isOpen && 'rotate-90')} />
                            </button>
                          )}
                        </td>
                        <td className="whitespace-nowrap px-2 py-3 text-sm text-muted num">{formatDateTime(e.createdAt)}</td>
                        <td className="px-3 py-3">
                          <p className="leading-snug">{e.summary}</p>
                          <p className="text-sm text-muted">{actionLabel(e.action)}</p>
                        </td>
                        <td className="px-3 py-3 text-sm">{e.userName ?? 'System'}</td>
                        <td className="px-5 py-3 text-right text-sm text-faint num">{e.seq}</td>
                      </tr>
                      {isOpen && (
                        <tr className="border-b border-line bg-paper/70">
                          <td />
                          <td colSpan={4} className="px-2 py-4 pr-5">
                            <AuditDetail entry={e} />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {q.hasNextPage && (
          <div className="flex justify-center border-t border-line p-4">
            <Button onClick={() => q.fetchNextPage()} busy={q.isFetchingNextPage}>
              Load older entries
            </Button>
          </div>
        )}
      </Panel>
    </>
  );
}

function AuditDetail({ entry }: { entry: AuditEntry }) {
  const before = (entry.before ?? {}) as Record<string, unknown>;
  const after = (entry.after ?? {}) as Record<string, unknown>;
  const keys = [...new Set([...Object.keys(before), ...Object.keys(after)])];
  return (
    <div className="space-y-3 text-sm">
      {keys.length > 0 && (
        <table className="w-full max-w-3xl">
          <thead>
            <tr className="text-muted">
              <th className="py-1 pr-4 text-left font-medium">Field</th>
              {entry.before != null && <th className="py-1 pr-4 text-left font-medium">Before</th>}
              {entry.after != null && <th className="py-1 text-left font-medium">After</th>}
            </tr>
          </thead>
          <tbody>
            {keys.map((k) => (
              <tr key={k} className="border-t border-line">
                <td className="py-1.5 pr-4 align-top text-muted">{k}</td>
                {entry.before != null && <td className="py-1.5 pr-4 align-top break-all">{show(before[k])}</td>}
                {entry.after != null && <td className="py-1.5 align-top break-all">{show(after[k])}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {entry.ip && <p className="text-muted">From network address {entry.ip}</p>}
    </div>
  );
}

function show(v: unknown): string {
  if (v === undefined || v === null || v === '') return '—';
  if (Array.isArray(v)) return v.length ? v.join(', ') : '(none)';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}
